/**
 * Matches a copied row grid onto the destination table's columns (SYD-2314).
 *
 * Pure: no React, no Valtio. Consumed by `useArTableClipboard` before it hands
 * the grid to `actions.pasteRows`, which expects cells already matched and
 * already coerced.
 *
 * Matching is by `typeId` and never by position, so a column with no
 * counterpart in the destination is dropped rather than mis-assigned — the same
 * code path within one table and across two. A row whose every cell is dropped
 * still becomes a row: the rule drops columns, not rows.
 *
 * `PastedRow` lives here rather than in `useArTableActions`, so this module
 * depends on no React hook; `useArTableActions` imports it back and re-exports it.
 */

import { resolveColumn } from './arCellClipboard';
import { coerceToColumnValue } from './coerceToColumnValue';
import type { ArCellClipboardEnvelope } from './arCellClipboard';
import type { ArCellValue, ArTableType, TypeId } from '#types/System.Artifacts.types';
import type { FieldType } from '#types/System.types';

/** One pasted row: destination column id to cell value, already matched and coerced. */
type PastedRow = Record<TypeId, ArCellValue>;

type ResolveRowPasteArgs = {
  rows: readonly (readonly ArCellClipboardEnvelope[])[];
  schemaData: ArTableType | null | undefined;
  fieldTypes: Record<string, FieldType>;
};

type ResolvedRowPaste = {
  /** One entry per source row, cells keyed by DESTINATION column id. */
  grid: PastedRow[];
  /** Source cells with no destination column, or a destination that refuses pastes. */
  skipped: number;
  /**
   * Always `0` here. A Form Derive projection owns a cell of the destination
   * ROW, and these rows do not exist yet, so nothing at this stage can say which
   * cells it would claim.
   */
  derived: number;
};

function resolveRowPaste(args: ResolveRowPasteArgs): ResolvedRowPaste {
  const { rows, schemaData, fieldTypes } = args;
  const destinationTypeIds = new Set((schemaData?.columns ?? []).map((column) => column.typeId));

  const resolved = rows.map((envelopes) => {
    const cells = envelopes.flatMap((envelope): [TypeId, ArCellValue][] => {
      const typeId = envelope.typeId;
      if (typeId == null || !destinationTypeIds.has(typeId)) return [];
      const target = resolveColumn(schemaData, fieldTypes, typeId);
      if (!target?.pasteable) return [];
      const value = coerceToColumnValue({ kind: 'structured', envelope }, target);
      if (value === undefined) return [];
      return [[typeId, value]];
    });
    return { row: Object.fromEntries(cells) as PastedRow, skipped: envelopes.length - cells.length };
  });

  return {
    grid: resolved.map((entry) => entry.row),
    skipped: resolved.reduce((total, entry) => total + entry.skipped, 0),
    derived: 0,
  };
}

export { resolveRowPaste };
export type { PastedRow, ResolveRowPasteArgs, ResolvedRowPaste };
